import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import {
  CreateEventRequest,
  type CreateSessionEventRequest,
  EventRecord,
  UpdateEventRequest,
} from "@/shared/domain/events";
import {
  eventQueryKeys,
  type EventsRequest,
} from "@/shared/domain/events/hooks";
import { hasSessionEvents } from "@/shared/domain/orgs";
import { useOrgEventModels } from "@/shared/domain/orgs/hooks";
import { type Datasource } from "@/shared/state/visualization";
import { useDatasources } from "@/shared/state/visualization/hooks";

import { type Session } from "./EphemeralWorkspaceState";
import { useEphemeralWorkspaceState, useWorkspaceSessions } from "./hooks";

interface SelectOptions {
  datasetIds?: string[];
  fileIds?: string[];
  topicIds?: string[];
  messagePathIds?: string[];
}

/** The files of each session in the workspace, by session ID. */
type SessionFileIds = ReadonlyMap<string, ReadonlySet<string>>;

type UseWorkspaceEventsOptions = Partial<{
  select: SelectOptions;
}>;

const unique = (ids: string[]): string[] => [...new Set(ids)];

/**
 * The subset of {@link EventsRequest} a workspace produces. `EventsRequest`
 * leaves every field optional because other callers query by topic or message
 * path; a workspace always states each of its own fields, even when empty.
 */
type WorkspaceEventsRequest = EventsRequest & {
  datasetIds: string[];
  fileIds: string[];
  /**
   * Loaded sessions whose own events the workspace shows: those whose owning
   * org has session events.
   */
  sessionIds: string[];
};

/**
 * The workspace's event-query identity: the datasets, files and sessions that
 * define it. Dataset and directly-named file datasources are stated at
 * datasource level (a dataset with thousands of files is one association, not
 * thousands).
 *
 * A session datasource is a reference with no files of its own, so the dataset
 * events on its data come from `sessionFiles`, the files `WorkspaceDataService`
 * resolved for every loaded session. Those land after the workspace's files
 * resolve, so unlike the datasource-only ids, this part of the request (and
 * its query key) changes once per load; the events query refetches when
 * session files arrive. The events created on the session itself come from
 * `sessionIds`, which names each loaded session whose own org has session
 * events (`orgHasSessionEvents`): a session in another org is judged by that
 * org, not the current one. A session datasource that failed to load is left
 * out: listing its events would fail too, and with it the whole request, the
 * other sources' events included.
 *
 * A session file is left out when a dataset datasource already covers it.
 * `Event.get_for_workspace` matches `dataset_ids` transitively (the dataset
 * itself plus every file, topic, and message path under it), so naming a file
 * whose parent dataset is already a datasource adds an id to the request
 * without adding scope. Left in, a session contributes one id per file to both
 * the request and its query key, thousands of them for a large session. A file
 * the user picked by hand is always named, even when its dataset is also a
 * datasource: one such file costs the request a single id.
 *
 * Leaving a file out can never empty the request: it happens only when that
 * file's dataset is in `datasetIds`. An empty request short-circuits to `[]` in
 * `EventService.getWorkspaceEvents`, so an event just created in the workspace
 * would otherwise vanish on the next refetch.
 *
 * Exported for tests.
 */
export function deriveWorkspaceEventsRequest(
  datasources: Datasource[],
  sessions: Session[],
  orgHasSessionEvents: (orgId: string) => boolean,
): WorkspaceEventsRequest {
  const sessionFiles = sessions.flatMap((session) => session.files);
  const datasetIds = unique(
    datasources
      .filter((datasource) => datasource.type === "dataset")
      .map((datasource) => datasource.value.datasetId),
  );

  const directFileIds = datasources
    .filter((datasource) => datasource.type === "file")
    .map((datasource) => datasource.value.fileId);

  const datasetSourceIds = new Set(datasetIds);
  const sessionFileIds = sessionFiles
    // A file record's `association_id` is the dataset holding it.
    .filter((file) => !datasetSourceIds.has(file.association_id))
    .map((file) => file.file_id);

  const sessionIds = unique(
    sessions
      .filter((session) => orgHasSessionEvents(session.record.org_id))
      .map((session) => session.record.session_id),
  );

  return {
    datasetIds,
    fileIds: unique([...directFileIds, ...sessionFileIds]),
    sessionIds,
  };
}

/**
 * Memoized {@link deriveWorkspaceEventsRequest} over the open workspace. Shared
 * between the query and the mutation hooks so their optimistic cache writes land
 * on the same cache slot.
 */
function useWorkspaceEventsRequest(): WorkspaceEventsRequest {
  const datasources = useDatasources();
  const sessions = useWorkspaceSessions();
  // `/v1/sessions/id/<id>/events` refuses every request for a session in an
  // org without session events, which would fail the workspace's dataset
  // events too. Each session is judged by its own org, which need not be the
  // current one.
  const sessionOrgIds = React.useMemo(
    () => unique(sessions.map((session) => session.record.org_id)),
    [sessions],
  );
  const eventModels = useOrgEventModels(sessionOrgIds);

  return React.useMemo(
    () =>
      deriveWorkspaceEventsRequest(datasources, sessions, (sessionOrgId) =>
        hasSessionEvents(eventModels[sessionOrgId]),
      ),
    [datasources, sessions, eventModels],
  );
}

/** {@link SessionFileIds} of the workspace's loaded sessions. */
function useSessionFileIds(): SessionFileIds {
  const sessions = useWorkspaceSessions();
  return React.useMemo(
    () =>
      new Map(
        sessions.map((session) => [
          session.record.session_id,
          new Set(session.files.map((file) => file.file_id)),
        ]),
      ),
    [sessions],
  );
}

export function useWorkspaceEvents(options?: UseWorkspaceEventsOptions) {
  const { events } = useDomainServices();
  const orgId = useCurrentOrgId();

  const { isDataLoading } = useEphemeralWorkspaceState();
  const workspaceRequest = useWorkspaceEventsRequest();
  const sessionFileIds = useSessionFileIds();

  return useQuery({
    queryKey: eventQueryKeys.forWorkspace(orgId, workspaceRequest),
    // One viz-specific request describing the workspace's data sources. The server
    // expands datasets/files transitively, infers each loose file's parent dataset to
    // include its dataset-wide events, and dedupes — so there's nothing to merge here.
    queryFn: ({ signal }) => {
      return events.getWorkspaceEvents(
        {
          datasetIds: workspaceRequest.datasetIds,
          fileIds: workspaceRequest.fileIds,
          sessionIds: workspaceRequest.sessionIds,
        },
        // The org scopes which custom-field schema the events come back with.
        { abortSignal: signal, resourceOwnerId: orgId },
      );
    },
    select: (data) => {
      if (options?.select) {
        return selectEvents(data, options.select, sessionFileIds);
      }

      return data;
    },
    enabled: !isDataLoading,
    staleTime: 300_000, // 5 minutes
  });
}

export function useWorkspaceEvent(eventId: string) {
  const { events } = useDomainServices();
  const orgId = useCurrentOrgId();

  return useQuery({
    queryKey: eventQueryKeys.detail(orgId, eventId),
    queryFn: ({ signal }) => {
      return events.getEventById(eventId, {
        abortSignal: signal,
      });
    },
    staleTime: 300_000, // 5 minutes
  });
}

export function useCreateWorkspaceEvent() {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();

  const orgId = useCurrentOrgId();
  const workspaceRequest = useWorkspaceEventsRequest();
  const sessions = useWorkspaceSessions();

  return useMutation({
    mutationFn: function createWorkspaceEvent(
      request: CreateEventRequest | CreateSessionEventRequest,
    ) {
      // A session event belongs to its session's org, which need not be the
      // current one; a dataset event belongs to the current org.
      const resourceOwnerId =
        "session_id" in request
          ? sessions.find(
              ({ record }) => record.session_id === request.session_id,
            )?.record.org_id
          : undefined;
      return events.createEvent(orgId, request, { resourceOwnerId });
    },
    onSuccess: (event: EventRecord) => {
      queryClient.setQueryData(
        // The workspace's events query is keyed by the current org, even when
        // the event belongs to a session of another org.
        eventQueryKeys.forWorkspace(orgId, workspaceRequest),
        (oldData: EventRecord[] | undefined) => {
          if (!oldData) {
            return undefined;
          }
          return [...oldData, event];
        },
      );
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(event.org_id),
      });
      // Other workspace views may include this event via its
      // associations or session — mark them stale for next mount. `refetchType:
      // "none"` avoids re-fetching the currently-active workspace,
      // which we just updated via `setQueryData` above.
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(event.org_id),
        refetchType: "none",
      });
    },
  });
}

export function useDeleteWorkspaceEvent() {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();
  const orgId = useCurrentOrgId();

  const workspaceRequest = useWorkspaceEventsRequest();

  return useMutation<void, Error, EventRecord["event_id"]>({
    mutationFn: function deleteWorkspaceEvent(eventId: string) {
      return events.deleteEvent(eventId);
    },
    onSuccess: (_, eventId) => {
      queryClient.setQueryData(
        eventQueryKeys.forWorkspace(orgId, workspaceRequest),
        (oldData: EventRecord[] | undefined) => {
          if (!oldData) {
            return undefined;
          }
          return oldData.filter((event) => event.event_id !== eventId);
        },
      );
      // The deleted event may also appear in other list views
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(orgId),
      });
      // Other workspace views may also include this event — mark stale
      // for next mount. `refetchType: "none"` avoids re-fetching the
      // currently-active workspace, which we just updated via
      // `setQueryData` above.
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(orgId),
        refetchType: "none",
      });
    },
  });
}

export function useUpdateWorkspaceEvent() {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();
  const orgId = useCurrentOrgId();

  const workspaceRequest = useWorkspaceEventsRequest();

  return useMutation<
    EventRecord,
    Error,
    { eventId: string; request: UpdateEventRequest }
  >({
    mutationFn: function updateWorkspaceEvent({ eventId, request }) {
      return events.updateEvent(eventId, request);
    },
    onSuccess: (event: EventRecord) => {
      queryClient.setQueryData(
        eventQueryKeys.forWorkspace(orgId, workspaceRequest),
        (oldData: EventRecord[] | undefined) => {
          if (!oldData) {
            return undefined;
          }

          return oldData.map((oldEvent) => {
            if (oldEvent.event_id === event.event_id) {
              return event;
            }
            return oldEvent;
          });
        },
      );

      queryClient.setQueryData(
        eventQueryKeys.detail(orgId, event.event_id),
        event,
      );

      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(orgId),
      });
      // Other workspace views may also include this event — mark stale
      // for next mount. `refetchType: "none"` avoids re-fetching the
      // currently-active workspace, which we just updated via
      // `setQueryData` above.
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(orgId),
        refetchType: "none",
      });
    },
  });
}

/**
 * The events a panel shows: dataset events associated with any of the
 * panel's datasets, files, topics or message paths, and session events whose
 * session holds any of the panel's files.
 *
 * A session event's topics name topic identities, which the panel's topic IDs
 * cannot be matched against, so its topics do not narrow it further.
 */
function selectEvents(
  events: EventRecord[],
  select: SelectOptions,
  sessionFileIds: SessionFileIds,
) {
  return events.filter((event) => {
    const {
      datasetIds = [],
      fileIds = [],
      topicIds = [],
      messagePathIds = [],
    } = select;

    if (event.session_id !== undefined) {
      const filesOfSession = sessionFileIds.get(event.session_id);
      return (
        filesOfSession !== undefined &&
        fileIds.some((fileId) => filesOfSession.has(fileId))
      );
    }

    if (
      datasetIds.length > 0 &&
      event.associations.some((association) =>
        datasetIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    if (
      fileIds.length > 0 &&
      event.associations.some((association) =>
        fileIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    if (
      topicIds.length > 0 &&
      event.associations.some((association) =>
        topicIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    if (
      messagePathIds.length > 0 &&
      event.associations.some((association) =>
        messagePathIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    return false;
  });
}
